import { useNavigate, useParams } from 'react-router-dom';
import { ContainerMenuPrint } from './styles';
import { AButton, ATitle, Loading, ToSignature } from '../../../components';
import {
  LeftCircleOutlined,
  PrinterOutlined,
  SignatureOutlined,
} from '@ant-design/icons';
import { TreatmentReport } from '../../../shared/reports';
import { useCallback, useEffect, useState } from 'react';
import {
  ILinkedTreatmentProduct,
  IRetailTreatmentItem,
  ITreatment,
  ITreatmentItem,
} from '../../../shared/interfaces';
import { useConfiguration } from '../../../hooks/configuration';
import { useToast } from '../../../hooks/toast';
import { getTreatment } from '../../../http';
import api from '../../../services/api';
import { hasAllowedModule } from '../../../shared/utils/modules';

const TreatmentPrint: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { configuration } = useConfiguration();
  const [treatment, setTreatment] = useState<ITreatment | null>(null);
  const [items, setItems] = useState<ITreatmentItem[]>([]);
  const [linkedProducts, setLinkedProducts] = useState<
    ILinkedTreatmentProduct[]
  >([]);
  const [retailItems, setRetailItems] = useState<IRetailTreatmentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [signerName, setSignerName] = useState<string | null>(null);
  const [signatureMode, setSignatureMode] = useState<string | null>(null);

  const loadTreatment = useCallback(async () => {
    try {
      const data = await getTreatment(id);
      const { treatment, items, linked_products, retail_items } = data;

      const isDigitallySigned = Boolean(
        treatment?.signature_validation_code || treatment?.signed_at,
      );

      let signedProfessionalName: string | null = null;
      let signedProfessionalSignature: string | null = null;

      if (treatment?.professional_id) {
        const professionalResponse = await api.get(
          `/professionals/${treatment.professional_id}`,
        );
        signedProfessionalName = professionalResponse.data?.name || null;

        if (isDigitallySigned) {
          signedProfessionalSignature =
            professionalResponse.data?.signature || null;
        }
      }

      setTreatment(treatment);
      setItems(items);
      setLinkedProducts(linked_products ?? []);
      setRetailItems(retail_items ?? []);
      setValidationCode(treatment?.signature_validation_code || null);
      setSignerName(treatment?.professional_name || signedProfessionalName);
      setSignature(signedProfessionalSignature);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: `${error?.response?.data?.error ?? 'Erro ao imprimir.'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTreatment();
  }, []);

  if (loading) return <Loading load />;

  const isDigitallySigned = Boolean(validationCode || treatment?.signed_at);
  const canUseDigitalSignature = hasAllowedModule(
    configuration?.allowed_modules,
    'assinatura_digital',
  );

  return (
    <>
      <ATitle icon={<PrinterOutlined />}>Imprimir Tratamento</ATitle>
      <ContainerMenuPrint>
        <AButton
          type="default"
          onClick={() => navigate('/treatments')}
          icon={<LeftCircleOutlined />}
        >
          Voltar
        </AButton>
        {!isDigitallySigned && canUseDigitalSignature && (
          <AButton
            type="dashed"
            onClick={() => setModalSign(true)}
            icon={<SignatureOutlined />}
            style={{ backgroundColor: '#33cf5c', color: '#fff' }}
          >
            Assinar tratamento
          </AButton>
        )}
      </ContainerMenuPrint>
      {validationCode && (
        <p style={{ marginTop: 8, fontWeight: 600 }}>
          Código de validação: {validationCode}
        </p>
      )}
      {signatureMode && (
        <p style={{ marginTop: 4, fontWeight: 600 }}>
          Modo da assinatura:{' '}
          {signatureMode === 'A1' ? 'Assinado com A1' : 'Assinatura interna'}
        </p>
      )}
      <TreatmentReport
        treatment={treatment}
        items={items}
        linkedProducts={linkedProducts}
        retailItems={retailItems}
        signerName={signerName}
        signature={signature}
        validationCode={validationCode || treatment?.signature_validation_code}
      />
      <ToSignature
        open={modalSign && canUseDigitalSignature}
        close={() => setModalSign(false)}
        professional_id={treatment?.professional_id}
        onAuthenticated={async (authData, password) => {
          const signResponse = await api.post('/signature-validation/sign', {
            document_type: 'TREATMENT',
            record_id: treatment?.id || id,
            professional_id: treatment?.professional_id,
            password,
          });

          setSignature(
            authData.signature ||
              signResponse.data.professional_signature ||
              null,
          );
          setValidationCode(signResponse.data.validation_code || null);
          setSignatureMode(signResponse.data.signature_mode || null);
          setSignerName(
            signResponse.data?.professional?.name ||
              treatment?.professional_name ||
              signerName,
          );
          addToast({
            type: 'success',
            title: 'Tratamento assinado',
            description: 'Tratamento assinado digitalmente com sucesso.',
          });
        }}
      />
    </>
  );
};

export default TreatmentPrint;
